/**
 * UserSelector - Component for selecting a single user
 */
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../../services/usersApi';
import { getTextFieldStyles, getAutocompleteStyles, getAutocompleteListboxStyles } from '../../styles/formStyles';

interface UserSelectorProps {
  value: number | null;
  onChange: (userId: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = '',
  label,
  error = false,
  helperText,
  required = false,
}) => {
  // Fetch all users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getUsers({ page_size: 1000 }), // Get all users
  });

  const users = usersData?.results || [];

  const selectedUser = users.find((u) => u.id === value) || null;

  const handleChange = (_event: any, newValue: typeof selectedUser) => {
    onChange(newValue ? newValue.id : null);
  };

  // Get display name for user
  const getOptionLabel = (option: typeof users[0]) => {
    if (option.name) return option.name;
    if (option.full_name) return option.full_name;
    if (option.first_name && option.last_name) {
      return `${option.first_name} ${option.last_name}`;
    }
    return option.username || option.email;
  };

  return (
    <Autocomplete
      options={users}
      value={selectedUser}
      onChange={handleChange}
      disabled={disabled || isLoading}
      loading={isLoading}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          fullWidth
          margin="normal"
          sx={getTextFieldStyles({ error })}
        />
      )}
      fullWidth
      sx={getAutocompleteStyles()}
      ListboxProps={{
        sx: getAutocompleteListboxStyles(),
      }}
    />
  );
};

export default UserSelector;
